import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { FlightDropdownOption } from 'components/Flights/types'
import type {
  FlightSearchWidgetFlightItem,
  FlightSearchWidgetInitialFocus,
} from 'contexts/Flights/FlightSearchWidget/types'
import { FlightsFareTypes } from 'constants/flight'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import { FlightSearchWidgetActions, createEmptyFlightSearchWidgetFlightItem } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import FareDropdownMobile from './FareDropdown/FareDropdownMobile'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FlightSearchTravellerSelectField from './FlightTravellerSelect/FlightSearchTravellerSelectField'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import FlightSearchMenuMobile from './FlightSearchMenu/FlightSearchMenuMobile'
import FlightSearchTravellerSelectContent from './FlightTravellerSelect/FlightSearchTravellerSelectContent'
import FlightSearchWidgetMobileForm from './FlightSearchWidgetMobileForm'
import FlightSearchWidgetMobileMultiCityForm from './FlightSearchWidgetMobileMultiCityForm'
import type { RecentFlightSearchesData } from 'hooks/Flights/useRecentFlightSearches'
import { RECENT_SEARCHES_STATUS } from 'hooks/Flights/useRecentFlightSearches'
import LineSeatIcon from 'LuxKit/Icons/line/LineSeatIcon'
import RecentFlightSearch from 'components/Flights/RecentFlightSearch/RecentFlightSearch'
import RecentFlightSearchSkeletonLoader from 'components/Flights/RecentFlightSearch/RecentFlightSearchSkeletonLoader'
import { rem } from 'polished'
import FlightSearchWidgetMobileResultsControls from './FlightSearchWidgetMobileResultsControls'

interface Props {
  /**
   * Sets the airports to a 'read only' mode
   * Used when defaulting airport selections such as on flight deal pages
   */
  readOnlyAirports?: boolean
  showMultiCity?: boolean
  recentSearchesData?: RecentFlightSearchesData
  initialFocus?: FlightSearchWidgetInitialFocus
  mobileResultsRedesign?: boolean
}

function FlightSearchWidgetMobile(props: Props) {
  const { readOnlyAirports, showMultiCity, initialFocus, mobileResultsRedesign } = props
  const intl = useIntl()

  const { fareTypes, fareCabins } = useFlightFareLabels()
  const state = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)
  const { data: recentSearches, status: recentSearchesStatus, shouldShowRecentSearches } = props.recentSearchesData || { }

  const { fareType, fareCabin } = state

  const changeFareType = useCallback(
    (fareType: FlightDropdownOption, prefillMultiCityDeparture = false) => {
      dispatch({ type: FlightSearchWidgetActions.SET_FARE_TYPE, fareType })

      // If it is multi-city or one way clear the checkout date
      if ([FlightsFareTypes.MULTI_CITY, FlightsFareTypes.ONE_WAY].includes(fareType.value as FlightsFareTypes)) {
        dispatch({ type: FlightSearchWidgetActions.SET_CHECKOUT_DATE, id: state.flights[0].id, date: null })
      }

      // If it is one-way or return we only need one flight
      if ([FlightsFareTypes.ONE_WAY, FlightsFareTypes.RETURN].includes(fareType.value as FlightsFareTypes)) {
        for (let i = 1; i < state.flights.length; i++) {
          const flight = state.flights[i]
          dispatch({ type: FlightSearchWidgetActions.REMOVE_FLIGHT, payload: flight.id })
        }
      }

      // If it is multi-city we show two flights
      if (fareType.value === FlightsFareTypes.MULTI_CITY && state.flights.length === 1) {
        const item: FlightSearchWidgetFlightItem = createEmptyFlightSearchWidgetFlightItem({
          ...(prefillMultiCityDeparture && { departureAirport: state.flights[0].arrivalAirport }),
        })
        dispatch({ type: FlightSearchWidgetActions.ADD_FLIGHT, payload: item })
      }
    },
    [dispatch, state.flights],
  )

  const changeFareTypeForMobileResults = useCallback((fareType: FlightDropdownOption) => {
    changeFareType(fareType, true)
  }, [changeFareType])

  const changeFareCabin = useCallback(
    (fareCabin: FlightDropdownOption) => {
      dispatch({ type: FlightSearchWidgetActions.SET_FARE_CABIN, fareCabin })
    },
    [dispatch],
  )

  const showModal = useContext(ModalContext)

  const travellersTitle = useMemo(
    () =>
      intl.formatMessage({
        id: 'M-8hnlubARNYJJXZe8dP',
        defaultMessage: 'Travellers',
        description: 'Title for travellers selection modal in flight search widget',
      }),
    [intl],
  )

  const onTravellerSelect = useCallback(() => {
    showModal(
      <FlightSearchMenuMobile title={travellersTitle} showTravellers>
        <FlightSearchTravellerSelectContent />
      </FlightSearchMenuMobile>,
    )
  }, [showModal, travellersTitle])

  const isMultiCity = fareType.value === FlightsFareTypes.MULTI_CITY

  const fareOptions = useMemo(() => {
    if (!showMultiCity) {
      return fareTypes.filter((fare) => fare.value !== FlightsFareTypes.MULTI_CITY)
    }
    return fareTypes
  }, [showMultiCity, fareTypes])

  const flightTypeTitle = useMemo(
    () =>
      intl.formatMessage({
        id: 'BI1SOI8jcyOMJX8qSi7J',
        defaultMessage: 'Flight type',
        description: 'Title for flight type dropdown in flight search widget',
      }),
    [intl],
  )

  const classTitle = useMemo(
    () =>
      intl.formatMessage({
        id: 'LrcepgBgVroUXYfwBcke',
        defaultMessage: 'Class',
        description: 'Title for cabin class dropdown in flight search widget',
      }),
    [intl],
  )

  return (
    <VerticalSpacer gap={12}>
      {mobileResultsRedesign && <FlightSearchWidgetMobileResultsControls
        fareOptions={fareOptions}
        fareCabinOptions={fareCabins}
        onFareTypeChange={changeFareTypeForMobileResults}
      />}

      {!mobileResultsRedesign && <Group
        direction="horizontal"
        gap={0}
        verticalAlign="center"
      >
        <FareDropdownMobile
          options={fareOptions}
          title={flightTypeTitle}
          selectedOption={fareType}
          onOptionChange={changeFareType}
          name="type"
          fixedWidth
        />

        <FlightSearchTravellerSelectField
          onClick={onTravellerSelect}
          data-testid="travellers"
          hideLabel
        />

        <FareDropdownMobile
          options={fareCabins}
          title={classTitle}
          selectedOption={fareCabin}
          onOptionChange={changeFareCabin}
          name="fareCabin"
          fixedWidth
          alignEnd
          startIcon={<LineSeatIcon />}
        />
      </Group>}

      {!isMultiCity && (
        <FlightSearchWidgetMobileForm
          readOnlyAirports={readOnlyAirports}
          initialFocus={initialFocus}
          mobileResultsRedesign={mobileResultsRedesign}
        />
      )}

      {isMultiCity && (
        <FlightSearchWidgetMobileMultiCityForm
          readOnlyAirports={readOnlyAirports}
          initialFocus={initialFocus}
          mobileResultsRedesign={mobileResultsRedesign}
        />
      )}
      {recentSearchesStatus === RECENT_SEARCHES_STATUS.FETCHING && (
        <Group direction="vertical" gap={16} style={{ marginTop: rem(16) }}>
          <RecentFlightSearchSkeletonLoader />
          <RecentFlightSearchSkeletonLoader />
          <RecentFlightSearchSkeletonLoader />
        </Group>
      )}
      {shouldShowRecentSearches && recentSearches && recentSearchesStatus === RECENT_SEARCHES_STATUS.AVAILABLE && (
        <Group direction="vertical" gap={8}>
          {recentSearches.map((search, index) => (
            <RecentFlightSearch key={index} recentSearch={search} />
          ))}
        </Group>
      )}
    </VerticalSpacer>
  )
}

export default FlightSearchWidgetMobile
